<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import mcpServersAPI from 'dashboard/api/captain/mcpServers';

const props = defineProps({
  mcpServer: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainMcpServers/getUIFlags');

const isSubmitting = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
const isTesting = ref(false);
const testResult = ref(null);

const form = reactive({
  name: '',
  title: '',
  description: '',
  assistant_id: null,
  icon: '',
  enabled: true,
  transport_type: 'stdio',
  config: {},
});

// Initialize form if editing
onMounted(() => {
  if (props.mcpServer) {
    form.name = props.mcpServer.name || '';
    form.title = props.mcpServer.title || '';
    form.description = props.mcpServer.description || '';
    form.assistant_id = props.mcpServer.assistant_id || null;
    form.icon = props.mcpServer.icon || '';
    form.enabled = props.mcpServer.enabled !== false;
    form.transport_type = props.mcpServer.transport_type || 'stdio';
    form.config = { ...props.mcpServer.config } || {};
  }

  if (!assistants.value.length) {
    store.dispatch('captainAssistants/get');
  }
});

const transportTypes = [
  { value: 'stdio', label: t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT_TYPE.STDIO') },
  { value: 'sse', label: t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT_TYPE.SSE') },
  { value: 'http', label: t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT_TYPE.HTTP') },
];

const assistantOptions = computed(() =>
  assistants.value.map(assistant => ({
    value: assistant.id,
    label: assistant.name,
  }))
);

const isUrlTransport = computed(
  () => form.transport_type === 'sse' || form.transport_type === 'http'
);

const isStdioTransport = computed(() => form.transport_type === 'stdio');

const validationRules = {
  name: {
    required: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.NAME_REQUIRED'),
      required
    ),
  },
  title: {
    required: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.TITLE_REQUIRED'),
      required
    ),
  },
  description: {
    required: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.DESCRIPTION_REQUIRED'),
      required
    ),
    minLength: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.DESCRIPTION_MIN_LENGTH'),
      minLength(20)
    ),
  },
  assistant_id: {
    required: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.ASSISTANT_REQUIRED'),
      required
    ),
  },
  transport_type: {
    required: helpers.withMessage(
      t('CAPTAIN.MCP_SERVERS.VALIDATIONS.TRANSPORT_TYPE_REQUIRED'),
      required
    ),
  },
};

const v$ = useVuelidate(validationRules, form);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const handleTransportTypeChange = () => {
  // Reset config when transport type changes
  form.config = {};
};

const argsText = ref('');

// Initialize args text from config
watch(
  () => form.config.args,
  newVal => {
    if (Array.isArray(newVal)) {
      argsText.value = newVal.join('\n');
    }
  },
  { immediate: true }
);

// Update config args when text changes
watch(argsText, newVal => {
  if (isStdioTransport.value && newVal) {
    form.config.args = newVal.split('\n').filter(arg => arg.trim());
  }
});

const handleTest = async () => {
  try {
    isTesting.value = true;
    testResult.value = null;

    const { data } = await mcpServersAPI.testConnection({
      mcp_server: form,
    });

    testResult.value = {
      success: true,
      tools_count: data.tools_count,
      tools: data.tools,
    };
    useAlert(
      t('CAPTAIN.MCP_SERVERS.TEST_CONNECTION.SUCCESS', {
        count: data.tools_count,
      })
    );
  } catch (error) {
    testResult.value = {
      success: false,
      error: error.response?.data?.error || error.message,
    };
    useAlert(t('CAPTAIN.MCP_SERVERS.TEST_CONNECTION.FAILED'));
  } finally {
    isTesting.value = false;
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  // Validate transport-specific config
  if (isUrlTransport.value && !form.config.url) {
    useAlert(t('CAPTAIN.MCP_SERVERS.VALIDATIONS.URL_REQUIRED'));
    return;
  }
  if (isStdioTransport.value && !form.config.command) {
    useAlert(t('CAPTAIN.MCP_SERVERS.VALIDATIONS.COMMAND_REQUIRED'));
    return;
  }

  emit('submit', form);
};

const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="handleSubmit">
    <!-- Basic Information Section -->
    <div class="flex flex-col gap-4">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.MCP_SERVERS.FORM.SECTIONS.BASIC_INFO') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label
          for="assistant"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('CAPTAIN.MCP_SERVERS.FORM.ASSISTANT.LABEL') }}
        </label>
        <ComboBox
          id="assistant"
          v-model="form.assistant_id"
          :options="assistantOptions"
          :has-error="!!getErrorMessage('assistant_id')"
          :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.ASSISTANT.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2"
          :message="getErrorMessage('assistant_id')"
        />
      </div>

      <Input
        v-model="form.title"
        :label="$t('CAPTAIN.MCP_SERVERS.FORM.TITLE.LABEL')"
        :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.TITLE.PLACEHOLDER')"
        :message="getErrorMessage('title')"
        :message-type="getErrorMessage('title') ? 'error' : 'info'"
      />

      <Input
        v-model="form.name"
        :label="$t('CAPTAIN.MCP_SERVERS.FORM.NAME.LABEL')"
        :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.NAME.PLACEHOLDER')"
        :message="
          getErrorMessage('name') || $t('CAPTAIN.MCP_SERVERS.FORM.NAME.HELP')
        "
        :message-type="getErrorMessage('name') ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CAPTAIN.MCP_SERVERS.FORM.DESCRIPTION.LABEL') }}
        </label>
        <textarea
          v-model="form.description"
          :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.DESCRIPTION.PLACEHOLDER')"
          class="block w-full reset-base text-sm px-3 py-2.5 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out resize-y min-h-[100px]"
          :class="[
            getErrorMessage('description')
              ? 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9'
              : 'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand',
          ]"
          rows="4"
        />
        <p
          class="text-xs"
          :class="
            getErrorMessage('description') ? 'text-n-ruby-9' : 'text-n-slate-11'
          "
        >
          {{
            getErrorMessage('description') ||
            $t('CAPTAIN.MCP_SERVERS.FORM.DESCRIPTION.HELP')
          }}
        </p>
      </div>

      <Input
        v-model="form.icon"
        :label="$t('CAPTAIN.MCP_SERVERS.FORM.ICON.LABEL')"
        :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.ICON.PLACEHOLDER')"
      />

      <div class="flex items-center gap-2">
        <Switch v-model="form.enabled" />
        <div class="flex flex-col">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.MCP_SERVERS.FORM.ENABLED.LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ $t('CAPTAIN.MCP_SERVERS.FORM.ENABLED.HELP') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Transport Configuration Section -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.MCP_SERVERS.FORM.SECTIONS.TRANSPORT_CONFIG') }}
      </h3>

      <div class="flex flex-col gap-1">
        <label
          for="transportType"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT_TYPE.LABEL') }}
        </label>
        <ComboBox
          id="transportType"
          v-model="form.transport_type"
          :options="transportTypes"
          class="[&>div>button]:bg-n-alpha-black2"
          @update:model-value="handleTransportTypeChange"
        />
      </div>

      <!-- URL Configuration (for SSE/HTTP) -->
      <template v-if="isUrlTransport">
        <Input
          v-model="form.config.url"
          :label="$t('CAPTAIN.MCP_SERVERS.FORM.URL.LABEL')"
          :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.URL.PLACEHOLDER')"
          :message="$t('CAPTAIN.MCP_SERVERS.FORM.URL.ERROR')"
          :message-type="!form.config.url ? 'error' : 'info'"
        />
      </template>

      <!-- STDIO Configuration -->
      <template v-if="isStdioTransport">
        <Input
          v-model="form.config.command"
          :label="$t('CAPTAIN.MCP_SERVERS.FORM.COMMAND.LABEL')"
          :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.COMMAND.PLACEHOLDER')"
          :message="$t('CAPTAIN.MCP_SERVERS.FORM.COMMAND.ERROR')"
          :message-type="!form.config.command ? 'error' : 'info'"
        />

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.MCP_SERVERS.FORM.ARGS.LABEL') }}
          </label>
          <textarea
            v-model="argsText"
            :placeholder="$t('CAPTAIN.MCP_SERVERS.FORM.ARGS.PLACEHOLDER')"
            class="block w-full reset-base text-sm px-3 py-2.5 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 transition-all duration-500 ease-in-out resize-y min-h-[100px] font-mono outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand"
            rows="4"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('CAPTAIN.MCP_SERVERS.FORM.ARGS.HELP') }}
          </p>
        </div>
      </template>
    </div>

    <!-- Test Results -->
    <div v-if="testResult" class="pt-4 border-t border-n-weak">
      <div
        class="p-4 rounded-lg"
        :class="[
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20'
            : 'bg-red-50 dark:bg-red-900/20',
        ]"
      >
        <h4
          class="text-sm font-medium mb-2"
          :class="
            testResult.success
              ? 'text-green-900 dark:text-green-100'
              : 'text-red-900 dark:text-red-100'
          "
        >
          {{
            testResult.success
              ? $t('CAPTAIN.MCP_SERVERS.TEST_CONNECTION.SUCCESS', {
                  count: testResult.tools_count,
                })
              : $t('CAPTAIN.MCP_SERVERS.TEST_CONNECTION.FAILED')
          }}
        </h4>
        <div
          v-if="testResult.success"
          class="text-xs space-y-1 max-h-48 overflow-auto"
        >
          <div
            v-for="(tool, index) in testResult.tools"
            :key="index"
            class="text-green-800 dark:text-green-200"
          >
            <strong>{{ tool.name }}</strong>: {{ tool.description }}
          </div>
        </div>
        <pre
          v-else
          class="text-xs overflow-auto max-h-48 text-red-800 dark:text-red-200"
          >{{ testResult.error }}</pre>
      </div>
    </div>

    <!-- Form Actions -->
    <div
      class="flex items-center justify-between gap-3 pt-4 border-t border-n-weak"
    >
      <Button
        type="button"
        variant="hollow"
        color="slate"
        :label="$t('CAPTAIN.MCP_SERVERS.TEST_CONNECTION.BUTTON')"
        :is-loading="isTesting"
        :disabled="isTesting || isSubmitting"
        @click="handleTest"
      />
      <div class="flex gap-3">
        <Button
          type="button"
          variant="hollow"
          color="slate"
          :label="$t('CAPTAIN.MCP_SERVERS.FORM.CANCEL')"
          :disabled="isSubmitting"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="
            mcpServer
              ? $t('CAPTAIN.MCP_SERVERS.FORM.UPDATE')
              : $t('CAPTAIN.MCP_SERVERS.FORM.CREATE')
          "
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
        />
      </div>
    </div>
  </form>
</template>
